import { Dispatch, SetStateAction } from 'react';
import { FormInstance } from 'antd';
import { TableProps } from 'antd/lib/table';
import clsx from 'clsx';

import { IInitialTableActions, InitialTableActions, TEditableColumn } from 'types';

import { CRUDTableEditingActions } from '../CRUDTableEditingActions';
import { CRUDTableInitialActions } from '../CRUDTableInitialActions';

export const getIsFieldEditing = (recordKey: number, editingRowKey?: React.Key) => {
  if (editingRowKey || editingRowKey === 0) {
    return recordKey === editingRowKey;
  }

  return false;
};

export const getClassName = <T extends Record<string, unknown>>(
  rowClassName?: TableProps<T>['rowClassName'],
  editingRowKey?: React.Key,
  getRowClassName?: (data: T, editingRowKey?: React.Key) => string,
) => (
  row: T,
) => {
  if (getRowClassName) {
    return getRowClassName(row, editingRowKey);
  }

  if (Object.hasOwn(row, 'isActive') && !row.isActive && editingRowKey !== Number(row.id)) {
    return '[&_*]:text-black/30';
  }

  return clsx(rowClassName, 'editable-table-row');
};

export const getColumnsWithCell = <T extends Record<string, unknown>, >(
  columns: TEditableColumn<T>[],
  editingRowKey?: number,
) => columns.map((column) => {
  if (!column.field) {
    return column;
  }

  const isFieldAdding = editingRowKey === 0;

  return {
    ...column,
    onCell: (record) => ({
      record,
      field: typeof column.field === 'function' ? column.field({ isFieldAdding, isFieldEditing: getIsFieldEditing(record.key as number, editingRowKey) }) : column.field,
      isFieldEditing: getIsFieldEditing(record.key as number, editingRowKey),
      dataIndex: column.dataIndex,
      getStyles: column.getStyles,
    }),
  };
}) as TEditableColumn<T>[];

export const getColumnsWithActions = <T extends Record<string, unknown>, >({
  form,
  columns,
  isFormValid,
  editingRowKey,
  editableTableData = [],
  initialTableActions,
  setEditingKey,
  setIsFormValid,
  onPostTableData,
  onPatchTableData,
  onDeleteTableData,
  setEditableTableData,
}: {
  form: FormInstance<T>;
  columns: TEditableColumn<T>[];
  isFormValid: boolean;
  editingRowKey?: number;
  editableTableData?: T[];
  initialTableActions?: IInitialTableActions[];
  setEditingKey: (key?: number) => void;
  setIsFormValid: (status: boolean) => void;
  onPostTableData?: (data: T) => void;
  onPatchTableData?: (data: T, actionType?: InitialTableActions) => void;
  onDeleteTableData?: (id: number) => void;
  setEditableTableData: Dispatch<SetStateAction<T[] | undefined>>;
}) => {
  const actions: TEditableColumn<T>[] = initialTableActions?.length ? [{
    width: 120,
    fixed: 'right',
    title: 'Actions',
    dataIndex: 'actions',
    render: (_: string, record) => {
      const recordKey = record.key as number;

      const isFieldEditing = getIsFieldEditing(recordKey, editingRowKey);
      const isIncludesEditable = Object.keys(record).includes('isEditable');
      const isIncludesDeletable = Object.keys(record).includes('isDeletable');
      const isInitActionsDisabled = !!editingRowKey || editingRowKey === 0;

      if (isIncludesEditable && isIncludesDeletable && record.isDeletable && !record.isEditable) {
        return (
          <CRUDTableInitialActions
            record={record}
            disabled={isInitActionsDisabled}
            initialTableActions={[{ type: InitialTableActions.Delete }]}
            onDeleteTableData={onDeleteTableData}
          />
        );
      }

      if (isIncludesEditable && !record.isEditable) {
        return null;
      }

      if (isFieldEditing) {
        return (
          <CRUDTableEditingActions
            form={form}
            disabled={!isFormValid}
            recordKey={recordKey}
            editableTableData={editableTableData}
            setEditingKey={setEditingKey}
            setIsFormValid={setIsFormValid}
            onPostTableData={onPostTableData}
            onPatchTableData={onPatchTableData}
            setEditableTableData={setEditableTableData}
          />
        );
      }

      return (
        <CRUDTableInitialActions
          form={form}
          record={record}
          disabled={isInitActionsDisabled}
          initialTableActions={initialTableActions}
          setEditingKey={setEditingKey}
          onPatchTableData={onPatchTableData}
          onDeleteTableData={onDeleteTableData}
        />
      );
    },
  }] : [];

  const columnsWithActions: TEditableColumn<T>[] = [
    ...columns,
    ...actions,
  ];

  const columnsWithCell = getColumnsWithCell(columnsWithActions, editingRowKey);

  return columnsWithCell;
};
